/**
 * Imports necessary modules and components for setting up the Backstage app.
 * Includes core Backstage plugins, custom components, and theme configurations.
 */
import React from 'react';
import { Route } from 'react-router-dom';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
  SignInProviderConfig,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { PlaygroundPage } from '@dewise-devex/backstage-plugin-playground';
import { AzdevopsPage } from '@dewise-devex/backstage-plugin-azdevops';
import { MeeweeTimeRegistrationPage } from '@dewise-devex/backstage-plugin-meewee-time-registration';
import { MaterialIcon } from '@dewise-devex/backstage-plugin-mui-uikit-react';
import { ProductsPage } from '@dewise-devex/backstage-plugin-product-frontend';
import { AppThemeProvider } from './theme';
import { TechdocsHomePage } from './components/techdocs/TechdocsHomePage';
import { TechdocsReader } from './components/techdocs/TechdocsReader';
import { UserProfilePage } from '@dewise-devex/backstage-plugin-user-profile';
import { CatalogHomePage } from './components/catalog/catalog-pages/CatalogHomePage';
import { SettingsHomePage } from './components/settings/SettingsHomePage';
import { CatalogImportHomePage } from './components/catalog/catalog-pages/CatalogImportHomePage';
import { CustomerExperiencePage } from '@dewise-devex/backstage-plugin-customer-experience';
import { ProductWorkItemPage } from '@dewise-devex/backstage-plugin-product-work-item';
import { NotificationProvider } from '@dewise-devex/backstage-plugin-notifications-react';

/**
 * Configuration for the Microsoft sign-in provider.
 */
const microsoftProvider: SignInProviderConfig = {
  id: 'microsoft-auth-provider',
  title: 'Microsoft',
  message: 'Sign in using Microsoft',
  apiRef: microsoftAuthApiRef,
};

/**
 * Initializes the Backstage application with configured plugins, themes, and routes.
 */
const app = createApp({
  apis,
  components: {
    /**
     * Configures the custom `SignInPage` component for the application.
     */
    SignInPage: props => {
      return (
        <SignInPage
          {...props}
          provider={microsoftProvider}
          title="Select a sign-in method"
          align="center"
          onSignInSuccess={authApi => {
            props.onSignInSuccess({
              getProfileInfo() {
                return authApi.getProfileInfo();
              },
              getBackstageIdentity() {
                return authApi.getBackstageIdentity();
              },
              getCredentials() {
                return authApi.getCredentials();
              },
              async signOut() {
                await authApi.signOut();
                localStorage.removeItem('authState');
              },
            });
          }}
        />
      );
    },
  },
  /**
   * Binds external plugin routes to their respective plugin entry points.
   */
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  /**
   * Defines light and dark themes for the application.
   */
  themes: [
    {
      id: 'light',
      title: 'Light',
      variant: 'light',
      icon: <MaterialIcon.light_mode />,
      Provider: ({ children }) => (
        <AppThemeProvider activeTheme="light" children={children} />
      ),
    },
    {
      id: 'dark',
      title: 'Dark',
      variant: 'dark',
      icon: <MaterialIcon.dark_mode />,
      Provider: ({ children }) => (
        <AppThemeProvider activeTheme="dark" children={children} />
      ),
    },
  ],
});

/**
 * Defines the application's routes using `FlatRoutes`.
 */
const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={<CatalogIndexPage initiallySelectedFilter="owned" />}
    >
      <CatalogHomePage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <TechdocsHomePage />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      {TechdocsReader}
    </Route>
    <Route path="/create" element={<ScaffolderPage />} />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
          <CatalogImportHomePage />
        </RequirePermission>
      }
    >
      {CatalogImportHomePage}
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<SettingsHomePage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/playground" element={<PlaygroundPage />} />
    <Route path="/azdevops" element={<AzdevopsPage />} />
    <Route
      path="/meewee-time-registration"
      element={<MeeweeTimeRegistrationPage />}
    />
    <Route path="/achievements" element={<ProductsPage />} />
    <Route path="/user-profile" element={<UserProfilePage />} />
    <Route path="/customer" element={<CustomerExperiencePage />} />
    <Route path="/work-item" element={<ProductWorkItemPage />} />
    <Route path="/work-item/:id" element={<ProductWorkItemPage />} />
  </FlatRoutes>
);

/**
 * Creates and exports the root of the Backstage application.
 *
 * The root includes the application theme provider, alert display, OAuth request dialog, and router.
 *
 * Features:
 * - `AppThemeProvider` for custom theme configurations
 * - `AlertDisplay` for displaying alerts
 * - `OAuthRequestDialog` for handling OAuth requests
 * - `AppRouter` for routing to different pages
 * - `Root` for rendering the application routes
 *
 * @category App
 * @returns The root of the Backstage application.
 */
export default app.createRoot(
  <AppThemeProvider>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>
        <NotificationProvider>{routes}</NotificationProvider>
      </Root>
    </AppRouter>
  </AppThemeProvider>,
);
